import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import {
  categories,
  articles,
  brandFeatures,
  testimonials,
  cultureCards,
  franchiseInfo,
  faqs,
} from '@/mocks/home';

function HeroSection() {
  const { t } = useTranslation();

  return (
    <section className="relative w-full h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20artistic%20abstract%20background%20with%20soft%20cream%2C%20amber%2C%20and%20terracotta%20tones%2C%20inspired%20by%20soy%20milk%20and%20traditional%20Chinese%20tofu%20pudding%20textures%2C%20smooth%20flowing%20organic%20shapes%2C%20minimalist%20style%2C%20warm%20atmospheric%20gradient%2C%20no%20text%2C%20no%20objects%2C%20pure%20abstract%20composition%20with%20gentle%20curves%20and%20soft%20light%20diffusion&width=1920&height=1080&seq=hero-bg-01&orientation=landscape"
          alt="91豆花"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
      </div>

      <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 text-center">
        <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4 md:mb-6 leading-tight">
          {t('hero.title')}
        </h1>
        <p className="text-sm md:text-base text-background-200 max-w-2xl mx-auto mb-8 md:mb-10 leading-relaxed">
          {t('hero.subtitle')}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/category/tian-douhua"
            className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            {t('hero.cta_explore')}
            <i className="ri-arrow-right-line"></i>
          </Link>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 px-8 py-3 border border-background-50/40 text-background-50 rounded-full text-sm font-medium hover:bg-background-50/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            {t('hero.cta_franchise')}
            <i className="ri-store-2-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}

function FeaturesSection() {
  const { t } = useTranslation();

  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
        <div className="text-center mb-12 md:mb-14">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            {t('features.title')}
          </h2>
          <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
            {t('features.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {brandFeatures.map((feature, idx) => (
            <div
              key={idx}
              className="group p-6 md:p-8 rounded-lg bg-background-100/70 hover:bg-background-100 transition-all duration-300 text-center"
            >
              <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                <i className={`${feature.icon} text-xl md:text-2xl`}></i>
              </div>
              <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2">
                {feature.title}
              </h3>
              <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function RecipesSection() {
  const { t } = useTranslation();
  const displayRecipes = articles.slice(0, 4);

  return (
    <section className="py-16 md:py-20 bg-background-100/50">
      <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
        <div className="text-center mb-12 md:mb-14">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            {t('recipes.title')}
          </h2>
          <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
            {t('recipes.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {displayRecipes.map((recipe) => (
            <Link
              key={recipe.id}
              to={`/article/${recipe.slug}`}
              className="group rounded-lg overflow-hidden bg-background-50 hover:bg-background-50 transition-all duration-300 cursor-pointer"
            >
              <div className="relative w-full h-48 md:h-52 overflow-hidden">
                <img
                  src={recipe.image}
                  alt={recipe.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3">
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50">
                    {recipe.categoryName}
                  </span>
                </div>
              </div>
              <div className="p-4 md:p-5">
                <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200 line-clamp-1">
                  {recipe.title}
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed mb-3 line-clamp-2">
                  {recipe.excerpt}
                </p>
                <div className="flex items-center gap-3 text-xs text-foreground-400">
                  <span className="flex items-center gap-1">
                    <i className="ri-tools-line"></i>
                    {recipe.difficulty}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {recipe.prepTime}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/category/tian-douhua"
            className="inline-flex items-center gap-2 px-6 py-2.5 border border-primary-500 text-primary-600 rounded-full text-sm font-medium hover:bg-primary-50 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            {t('recipes.view_all')}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}

function CultureSection() {
  const { t } = useTranslation();

  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
        <div className="text-center mb-12 md:mb-14">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            {t('culture.title')}
          </h2>
          <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
            {t('culture.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {cultureCards.map((card, idx) => (
            <div key={idx} className="group rounded-lg overflow-hidden bg-background-100/70">
              <div className="relative w-full h-48 overflow-hidden">
                <img
                  src={card.image}
                  alt={card.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5 md:p-6">
                <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2">
                  {card.title}
                </h3>
                <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">
                  {card.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  const { t } = useTranslation();

  return (
    <section className="py-16 md:py-20 bg-background-100/50">
      <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
        <div className="text-center mb-12 md:mb-14">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            {t('testimonials.title')}
          </h2>
          <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
            {t('testimonials.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {testimonials.map((item, idx) => (
            <div key={idx} className="p-6 md:p-8 rounded-lg bg-background-50">
              <div className="flex items-center gap-1 mb-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i
                    key={i}
                    className={`ri-star-fill text-sm ${i < item.rating ? 'text-primary-500' : 'text-background-300'}`}
                  ></i>
                ))}
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed mb-5 italic">
                &ldquo;{item.comment}&rdquo;
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                  <img
                    src={item.avatar}
                    alt={item.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">{item.name}</p>
                  <p className="text-xs text-foreground-400">{item.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FranchiseSection() {
  const { t } = useTranslation();

  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          <div className="w-full lg:w-1/2">
            <div className="relative w-full h-64 md:h-80 lg:h-96 rounded-lg overflow-hidden">
              <img
                src={franchiseInfo.image}
                alt={t('franchise.title')}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="w-full lg:w-1/2">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
              {t('franchise.title')}
            </h2>
            <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-6">
              {franchiseInfo.description}
            </p>
            <ul className="flex flex-col gap-3 mb-8">
              {franchiseInfo.benefits.map((benefit, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-checkbox-circle-fill text-primary-500 text-lg"></i>
                  </div>
                  <span className="text-sm text-foreground-700">{benefit}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {t('franchise.cta')}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  const { t } = useTranslation();
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  return (
    <section className="py-16 md:py-20 bg-background-100/50">
      <div className="w-full px-4 md:px-6 lg:px-10 max-w-3xl mx-auto">
        <div className="text-center mb-12 md:mb-14">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            {t('faq.title')}
          </h2>
          <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
            {t('faq.subtitle')}
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {faqs.map((faq, idx) => (
            <div
              key={idx}
              className="rounded-lg bg-background-50 overflow-hidden"
            >
              <button
                type="button"
                className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
                onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
              >
                <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">
                  {faq.q}
                </span>
                <i
                  className={`ri-arrow-down-s-line text-lg text-foreground-400 transition-transform duration-300 flex-shrink-0 ${openIdx === idx ? 'rotate-180' : ''}`}
                ></i>
              </button>
              {openIdx === idx && (
                <div className="px-5 pb-5">
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {faq.a}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function NewsletterSection() {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const formData = new FormData(formEl);
    const honeypot = formData.get('phone_alt')?.toString().trim() ?? '';
    if (honeypot) {
      setStatus('success');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9pkbbmb4qjhggaepvtg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* raw text */ }
      const code = (parsed as { code?: string }).code;
      const metaMsg = (parsed as { meta?: { message?: string } }).meta?.message;
      const serverMsg = (parsed as { message?: string }).message ?? metaMsg ?? responseText;

      if (response.ok && code === 'OK') {
        setStatus('success');
        formEl.reset();
      } else {
        setStatus('error');
        setErrorMsg(typeof serverMsg === 'string' ? serverMsg : '订阅失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络错误，请检查您的连接后重试。');
    }
  };

  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="w-full px-4 md:px-6 lg:px-10 max-w-2xl mx-auto text-center">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
          {t('newsletter.title')}
        </h2>
        <p className="text-sm md:text-base text-foreground-500 mb-8">
          {t('newsletter.subtitle')}
        </p>

        {status === 'success' ? (
          <div className="py-4 px-6 rounded-lg bg-secondary-100 text-secondary-800 text-sm font-medium">
            <i className="ri-check-line mr-2"></i>
            订阅成功！感谢您的关注，我们会将最新食谱和优惠发送到您的邮箱。
          </div>
        ) : (
          <form ref={formRef} onSubmit={handleSubmit} data-readdy-form="" className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              name="email"
              required
              placeholder={t('footer.newsletter_placeholder')}
              className="flex-1 px-5 py-3 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
            />
            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="honeypot-field"
            />
            <button
              type="submit"
              className="px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {t('footer.newsletter_btn')}
            </button>
          </form>
        )}

        {status === 'error' && (
          <p className="mt-4 text-sm text-accent-600">{errorMsg}</p>
        )}
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main>
      <Navbar />
      <HeroSection />
      <FeaturesSection />
      <RecipesSection />
      <CultureSection />
      <TestimonialsSection />
      <FranchiseSection />
      <FaqSection />
      <NewsletterSection />
      <Footer />
    </main>
  );
}